<template>
    <div class="main_search flex-1-1-100" :class="mobile ? 'mobile px-5' : 'desktop justify-end'">
        <v-text-field
            id="searchField"
            variant="outlined"
            autocomplete="new-search"
            :placeholder="placeholder"
            :class="{
                'rounded-0' : $vuetify.display.smAndDown,
                'text-search-box': !!modelValue
            }"
            :rounded="5"
            v-model="modelValue"
            :hide-details="true"
            v-on:keyup.enter="searchMore"
            @update:modelValue="startSearching"
            @click.prevent="keepMenuOpen"
        >
            <template v-slot:append>
                <v-btn class="text-body-1 px-8 h-100 bg-orange text-white" @click="searchMore">{{ $t('search.action') }}</v-btn>
            </template>
            <template v-slot:prepend-inner>
                <v-icon :icon="mdiMagnify" class="ml-4"></v-icon>
            </template>
            <template v-slot:append-inner>
                <v-icon v-if="modelValue.length > 0" :icon="mdiCloseCircle" class="mr-3" @click="searchStop"></v-icon>
                <v-icon v-show="$vuetify.display.mdAndDown" :icon="mdiMagnify" class="mr-4"></v-icon>
            </template>

        </v-text-field>
        <v-overlay
            attach=".main_search"
            v-model="overlayActive"
            :persistent="true"
            :close-on-content-click="false"
        >
            <v-menu
                v-if="show && modelValue && (hits.length > 0 || hits.notFound)"
                v-model="menuModel"
                activator="#searchField"
                class="c-search-menu full-overlay"
                :class="{'mobile-search-results': $vuetify.display.mdAndDown}"
                :close-on-content-click="false"
                :location-strategy="$vuetify.display.mdAndDown ? 'static' : connectedLocationStrategy"
                location="bottom center"
                theme="light"
            >
                <search-results
                    v-click-outside="searchPause" :query="modelValue" :hits="hits" @empty="modelValue=''" @close-results="menuModel = false"
                >
                </search-results>
            </v-menu>
        </v-overlay>
    </div>
</template>
<script setup>

import {
    mdiCloseCircle,
    mdiMagnify
} from "@mdi/js";
import {useSearchStore} from "./store/search";
import {debounce} from "lodash-es";
import {storeToRefs} from "pinia";
import pinia from "../../../plugins/pinia";
import {computed, inject, ref, watch} from "vue";
import {useDisplay} from "vuetify";
import {useOverlayStore} from "./store/overlay";
import SearchResults from "./SearchResults.vue";
import {router} from "@inertiajs/vue3";
import {trans} from "laravel-vue-i18n";
import { addEvent } from '../../../gtm.js';
import { connectedLocationStrategy } from '../../../utils/locationStrategy.js';

const props = defineProps({
    mobile: Boolean,
    show: Boolean,
    heightInfoBar: Number
});
const route = inject('route');
const overlayStore = useOverlayStore(pinia);
const {lgAndUp, smAndDown} = useDisplay()
const placeholder = computed(() => {
    if (lgAndUp.value) {
        return trans('search.search');
    }
    if (smAndDown.value) {
        return trans('search.search_compact');
    }
    return trans('search.search_comfortable');
})

const store = useSearchStore(pinia);
const {query: modelValue, hits} = storeToRefs(store);
const searching = ref(false);
const menuModel = ref(false);
let logTimeout = null;
const startSearching = debounce((query) => {
    clearTimeout(logTimeout);
    if(query) {
        logTimeout = setTimeout(() => {
            store.logQuery(query, '/log-search')
        }, 3500);
        searching.value = true;
        store.search(query, '/search').then(() => {
            searching.value = false;
            menuModel.value = true;
            addEvent({
                event: 'search',
                ecommerce: {
                    search_term: query,
                }
            })
        });
    }
}, 300)
const keepMenuOpen = () => {
    if (modelValue.value) {
        menuModel.value = true;
    }
}
const searchStop = () => {
    modelValue.value = '';
    menuModel.value = false;
}
const searchPause = () => {
    menuModel.value = false;
}
const overlayActive = ref(false);

watch(menuModel, (now) => {
    overlayStore.overlayMenu = now;
    overlayActive.value = now;
})
watch(modelValue, async (now) => {
    overlayStore.overlayMenu = (now.trim() !== '');
    overlayActive.value = (now.trim() !== '');
})

const searchMore = () => {
    overlayStore.overlayMenu = false;
    overlayActive.value = false;

    if (modelValue.value === '')
        return null;

    router.visit(route('search.view', { 'query': modelValue.value }));
    modelValue.value = '';

    setTimeout(() => {
        overlayStore.overlayMenu = false;
        overlayActive.value = false;
    }, 300)
}

</script>

<style scoped lang="scss">
@use '../../../plugins/vuetify-settings' as *;
@use 'vuetify/lib/styles/settings/variables' as *;
@use 'sass:map';
$marginFromTop: 98px;
$marginFromMdTop: 58.5px;

:deep(em) {
    font-weight: 700;
    background-color: map.get($orange, 'lighten-4') !important;
    border-radius: 5px;
    color: map.get($orange, 'base');
    font-style: normal;
}

.c-hit__text {
    &.is-hovering {
        :deep(em) {
            color: map.get($orange, darken-3);
        }
    }
}

ul {
    li + li {
        border-top: 1px solid map.get($grey, lighten-2);
    }
}

.is-hidden {
    :deep(.v-field__input) {
        visibility: hidden;
    }
}

.desktop {
    :deep(.v-input) {
        max-width: 100%;
        transition: max-width .3s ease;

        .v-icon {
            transition: color .3s ease;
        }

        .v-field {
            padding: 0 10px;
            //border: 1px solid map.get($grey, lighten-2);
            transition: border-color .5s ease;
            border-radius: var(--motonet-border-radius);
        }

        &.is-hidden {
            max-width: 50px;

            .v-field {
                border-color: transparent;
            }
        }
    }
}

.mobile-search-results {
    position: fixed;
    width: 100%;
    height: calc(100dvh - $marginFromTop);


    &::-webkit-scrollbar {
        display: none;
    }

    -ms-overflow-style: none;
    scrollbar-width: none;
    z-index: 2000;
    top: $marginFromTop;
    @media #{map.get($display-breakpoints, 'md-and-up')} {
        height: calc(100dvh - $marginFromMdTop);
        top: $marginFromMdTop;
    }

    .v-card {
        border-radius: 0 !important;
    }
}

.main_search {
    flex: 1 1 auto;
    max-width: 550px;

    &.mobile, &.desktop {
        > :deep(.v-input) {
            .v-field__field {
                padding-left: 10px;
                background-color: rgb(var(--v-theme-surface));

                .v-field__input {
                    padding-bottom: 0;
                }
            }

            .v-field {
                padding-inline: 0;
                .v-field__outline {
                    > * {
                        transition: border-color .3s ease;
                    }
                }

                &--focused {
                    .v-field__outline {
                        > * {
                            border-color: rgb(var(--v-theme-cis-accent-color));
                        }
                    }
                }
            }

            .v-field__clearable {
                margin: 0;

                .v-icon {
                    margin-left: 4px;
                }
            }

            .v-field__append-inner {
                align-self: stretch;
                cursor: pointer;
                color: black;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
            }

            .v-input__append {
                margin-inline-start: 0;
                color: map.get($orange, base);
                @media #{map.get($display-breakpoints, 'md-and-down')} {
                    display: none;
                }
            }

            .v-field__outline {
                .v-field__outline__end {
                    @media #{map.get($display-breakpoints, 'lg-and-up')} {
                        border-right: none;
                        border-top-right-radius: 0;
                        border-bottom-right-radius: 0;
                    }
                }
            }

            .v-field__prepend-inner {
                display: flex;
                align-items: center;
                justify-content: center;
                padding-block: 0;
                @media #{map.get($display-breakpoints, 'md-and-down')} {
                    display: none;
                }
            }
        }
    }

    > :deep(.v-input) {
        .v-field__clearable, .v-field__append-inner {
            padding-top: var(--v-field-input-padding-bottom);
            align-self: center;
        }

        .v-field__input {
            padding-top: calc(var(--v-input-chips-margin-bottom) + var(--v-field-input-padding-bottom));
        }
    }
}

.c-em {
    cursor: pointer;

    &:hover {
        opacity: .8;
    }
}

.c-link {
    cursor: pointer;

    &:hover {
        text-decoration: none !important;
        background-color: map.get($orange, 'lighten-4');
        padding: 2px 5px;
        border-radius: 5px;
        margin: -2px -5px;
        color: map.get($orange, 'base')
    }
}

.hints-card {
    transform: translate3d(0, 10px, 0);
    .v-avatar {
        flex: 0 0 auto;
    }

    > .v-card-text .scroll-area {
        max-height: 70vh;
        overflow-y: auto;
    }
}

.text-search-box {
    background: white;
    z-index: 9999;
    position: relative;
    border-radius: 5px;
}

</style>
